import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { 
  FileText, Download, Loader2, Plus, Trash2, Edit3, 
  Building2, Phone, Mail, Globe, MapPin, Image, Upload,
  CheckCircle, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default DHR Products
const DEFAULT_PRODUCTS = [
  { key: "fire_stop_sealant", name: "Fire Stop Sealant", name_ar: "مانع تسرب الحريق", description: "Professional fire safety sealant for penetration sealing in walls and floors", features: ["Fire ratings up to 4 hours", "UL Listed and FM Approved", "Easy application", "Smoke and gas tight"] },
  { key: "expansion_joint", name: "Expansion Joint Cover", name_ar: "غطاء فاصل التمدد", description: "Engineered systems for building movement and thermal expansion", features: ["Heavy-duty aluminum construction", "Movement capacity ±25mm to ±500mm", "Fire-rated options", "Watertight sealing"] },
  { key: "raised_floor", name: "Raised Floor System", name_ar: "نظام الأرضيات المرفوعة", description: "Modular access flooring for data centers and offices", features: ["Load capacity 500-3000 kg", "Anti-static options", "Easy cable management", "Various finishes available"] },
];

const ProfileTemplateGenerator = () => {
  // Company Info
  const [companyInfo, setCompanyInfo] = useState({
    name: 'Distinct High Road',
    name_ar: 'الطريق العالي المميز',
    tagline: 'Excellence in Construction Solutions',
    tagline_ar: 'التميز في حلول البناء',
    phone: '+966 11 XXX XXXX',
    email: 'info@dhr-sa.com',
    website: 'www.dhr-sa.com',
    address: 'Riyadh, Saudi Arabia'
  });

  // Products
  const [products, setProducts] = useState(DEFAULT_PRODUCTS);
  const [selectedProducts, setSelectedProducts] = useState(DEFAULT_PRODUCTS.map(p => p.key));
  
  // Add/Edit Product Form
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({
    name: '',
    name_ar: '',
    description: '',
    features: ''
  });

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [generatedPdf, setGeneratedPdf] = useState(null);

  // Product image uploads
  const [productImages, setProductImages] = useState({});

  const handleAddProduct = () => {
    if (!newProduct.name || !newProduct.description) {
      toast.error('يرجى إدخال اسم المنتج والوصف');
      return;
    }
    
    const key = `custom_${Date.now()}`;
    const featuresArray = newProduct.features.split(',').map(f => f.trim()).filter(f => f);
    
    const product = {
      key,
      name: newProduct.name,
      name_ar: newProduct.name_ar || newProduct.name,
      description: newProduct.description,
      features: featuresArray.length > 0 ? featuresArray : ['Professional grade', 'High quality']
    };
    
    setProducts([...products, product]);
    setSelectedProducts([...selectedProducts, key]);
    setNewProduct({ name: '', name_ar: '', description: '', features: '' });
    setShowAddProduct(false);
    toast.success('تم إضافة المنتج بنجاح! ✅');
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setNewProduct({
      name: product.name,
      name_ar: product.name_ar || '',
      description: product.description,
      features: product.features.join(', ')
    });
    setShowAddProduct(true);
  };

  const handleSaveEdit = () => {
    if (!newProduct.name || !newProduct.description) {
      toast.error('يرجى إدخال اسم المنتج والوصف');
      return;
    }
    
    const featuresArray = newProduct.features.split(',').map(f => f.trim()).filter(f => f);
    
    setProducts(products.map(p => 
      p.key === editingProduct.key 
        ? {
            ...p,
            name: newProduct.name,
            name_ar: newProduct.name_ar || newProduct.name,
            description: newProduct.description,
            features: featuresArray.length > 0 ? featuresArray : p.features
          }
        : p
    ));
    
    setNewProduct({ name: '', name_ar: '', description: '', features: '' });
    setEditingProduct(null);
    setShowAddProduct(false);
    toast.success('تم تحديث المنتج! ✅');
  };

  const handleDeleteProduct = (key) => {
    setProducts(products.filter(p => p.key !== key));
    setSelectedProducts(selectedProducts.filter(k => k !== key));
    toast.success('تم حذف المنتج');
  };

  const handleImageUpload = async (productKey, file) => {
    const formData = new FormData();
    formData.append('product_key', productKey);
    formData.append('image', file);

    try {
      const response = await api.post('/design/template/upload-product-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setProductImages({
          ...productImages,
          [productKey]: response.data.file_path
        });
        toast.success('تم رفع الصورة بنجاح!');
      }
    } catch (error) {
      toast.error('فشل رفع الصورة');
    }
  };

  const handleGenerateProfile = async () => {
    if (selectedProducts.length === 0) {
      toast.error('يرجى اختيار منتج واحد على الأقل');
      return;
    }

    setGenerating(true);
    setGeneratedPdf(null);

    try {
      const selectedProductsData = products
        .filter(p => selectedProducts.includes(p.key))
        .map(p => ({
          key: p.key,
          name: p.name,
          name_ar: p.name_ar,
          description: p.description,
          features: p.features,
          image_path: productImages[p.key] || null  // Include uploaded image path
        }));

      const formData = new URLSearchParams();
      formData.append('company_name', companyInfo.name);
      formData.append('company_name_ar', companyInfo.name_ar);
      formData.append('tagline', companyInfo.tagline);
      formData.append('tagline_ar', companyInfo.tagline_ar);
      formData.append('phone', companyInfo.phone);
      formData.append('email', companyInfo.email);
      formData.append('website', companyInfo.website);
      formData.append('address', companyInfo.address);
      formData.append('products_json', JSON.stringify(selectedProductsData));
      formData.append('product_images_json', JSON.stringify(productImages));  // Send all image paths

      const response = await api.post('/design/template/company-profile', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      if (response.data.success) {
        setGeneratedPdf(response.data);
        toast.success(`تم إنشاء البروفايل بنجاح! (${response.data.total_pages} صفحات) 📄`);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'فشل إنشاء البروفايل');
    } finally {
      setGenerating(false);
    }
  };

  const downloadPdf = () => {
    if (generatedPdf?.file_name) {
      window.open(`${API_URL}/api/design/template/profile/${generatedPdf.file_name}/download`, '_blank');
    }
  };

  const previewPdf = () => {
    if (generatedPdf?.file_name) {
      window.open(`${API_URL}/api/design/template/profile/${generatedPdf.file_name}`, '_blank');
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="h-7 w-7 text-blue-600" />
            مُنشئ Company Profile الاحترافي
          </h1>
          <p className="text-gray-500 mt-1">
            قوالب PDF احترافية جاهزة - بدون الحاجة لصور AI
          </p>
        </div>
        
        {generatedPdf && (
          <div className="flex gap-2">
            <Button onClick={previewPdf} variant="outline">
              <Eye className="h-4 w-4 ml-2" />
              معاينة
            </Button>
            <Button onClick={downloadPdf} className="bg-green-600 hover:bg-green-700">
              <Download className="h-4 w-4 ml-2" />
              تحميل PDF
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="h-5 w-5 text-blue-600" />
              معلومات الشركة
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">اسم الشركة (EN)</Label>
                <Input
                  value={companyInfo.name}
                  onChange={(e) => setCompanyInfo({...companyInfo, name: e.target.value})}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">اسم الشركة (AR)</Label>
                <Input
                  value={companyInfo.name_ar}
                  onChange={(e) => setCompanyInfo({...companyInfo, name_ar: e.target.value})}
                  className="h-8 text-sm"
                  dir="rtl"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">الشعار (EN)</Label>
                <Input
                  value={companyInfo.tagline}
                  onChange={(e) => setCompanyInfo({...companyInfo, tagline: e.target.value})}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">الشعار (AR)</Label>
                <Input
                  value={companyInfo.tagline_ar}
                  onChange={(e) => setCompanyInfo({...companyInfo, tagline_ar: e.target.value})}
                  className="h-8 text-sm"
                  dir="rtl"
                />
              </div>
            </div>

            <div className="pt-2 border-t">
              <p className="text-xs font-medium text-gray-500 mb-2">معلومات الاتصال</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <Input
                    value={companyInfo.phone}
                    onChange={(e) => setCompanyInfo({...companyInfo, phone: e.target.value})}
                    className="h-7 text-sm flex-1"
                    placeholder="رقم الهاتف"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <Input
                    value={companyInfo.email}
                    onChange={(e) => setCompanyInfo({...companyInfo, email: e.target.value})}
                    className="h-7 text-sm flex-1"
                    placeholder="البريد الإلكتروني"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-gray-400" />
                  <Input
                    value={companyInfo.website}
                    onChange={(e) => setCompanyInfo({...companyInfo, website: e.target.value})}
                    className="h-7 text-sm flex-1"
                    placeholder="الموقع الإلكتروني"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <Input
                    value={companyInfo.address}
                    onChange={(e) => setCompanyInfo({...companyInfo, address: e.target.value})}
                    className="h-7 text-sm flex-1"
                    placeholder="العنوان"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Products */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Image className="h-5 w-5 text-purple-600" />
                المنتجات ({selectedProducts.length} مختار)
              </CardTitle>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => {
                  setShowAddProduct(true);
                  setEditingProduct(null);
                  setNewProduct({ name: '', name_ar: '', description: '', features: '' });
                }}
                className="h-8"
              >
                <Plus className="h-4 w-4 ml-1" />
                إضافة منتج
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Add/Edit Form */}
            {showAddProduct && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                <p className="text-sm font-medium text-blue-700">
                  {editingProduct ? '✏️ تعديل المنتج' : '➕ إضافة منتج جديد'}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="اسم المنتج (بالإنجليزية) *"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                  />
                  <Input
                    placeholder="اسم المنتج (بالعربية)"
                    value={newProduct.name_ar}
                    onChange={(e) => setNewProduct({...newProduct, name_ar: e.target.value})}
                    dir="rtl"
                  />
                </div>
                <Textarea
                  placeholder="وصف المنتج (بالإنجليزية) *"
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                  rows={2}
                />
                <Input
                  placeholder="المميزات (مفصولة بفاصلة) - مثال: Feature 1, Feature 2, Feature 3"
                  value={newProduct.features}
                  onChange={(e) => setNewProduct({...newProduct, features: e.target.value})}
                />
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    onClick={editingProduct ? handleSaveEdit : handleAddProduct}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {editingProduct ? 'حفظ التعديل' : 'إضافة'}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      setShowAddProduct(false);
                      setEditingProduct(null);
                    }}
                  >
                    إلغاء
                  </Button>
                </div>
              </div>
            )}

            {/* Products List */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {products.map(product => (
                <div 
                  key={product.key} 
                  className={`p-3 rounded-lg border transition-colors ${
                    selectedProducts.includes(product.key) 
                      ? 'bg-blue-50 border-blue-200' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProducts([...selectedProducts, product.key]);
                        } else {
                          setSelectedProducts(selectedProducts.filter(k => k !== product.key));
                        }
                      }}
                      className="mt-1 rounded"
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{product.name}</span>
                        {product.name_ar && (
                          <span className="text-sm text-gray-500">({product.name_ar})</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{product.description}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {product.features.slice(0, 3).map((f, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {f}
                          </Badge>
                        ))}
                        {product.features.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{product.features.length - 3}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Image upload */}
                      <div className="mt-2 flex items-center gap-2">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                handleImageUpload(product.key, e.target.files[0]);
                              }
                            }}
                          />
                          <span className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700">
                            <Upload className="h-3 w-3" />
                            {productImages[product.key] ? 'تغيير الصورة' : 'رفع صورة'}
                          </span>
                        </label>
                        {productImages[product.key] && (
                          <span className="text-xs text-green-600 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            تم الرفع
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-1">
                      <button 
                        onClick={() => handleEditProduct(product)}
                        className="p-1.5 hover:bg-blue-100 rounded text-blue-600"
                        title="تعديل"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      {product.key.startsWith('custom_') && (
                        <button 
                          onClick={() => handleDeleteProduct(product.key)}
                          className="p-1.5 hover:bg-red-100 rounded text-red-600"
                          title="حذف"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Generate Button */}
            <div className="mt-4 pt-4 border-t">
              <Button 
                onClick={handleGenerateProfile}
                disabled={generating || selectedProducts.length === 0}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                size="lg"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-5 w-5 ml-2 animate-spin" />
                    جاري إنشاء البروفايل...
                  </>
                ) : (
                  <>
                    <FileText className="h-5 w-5 ml-2" />
                    إنشاء Company Profile ({selectedProducts.length + 4} صفحات)
                  </>
                )}
              </Button>
              <p className="text-xs text-center text-gray-500 mt-2">
                غلاف + فهرس + نبذة + منتجات ({selectedProducts.length}) + اتصل بنا
              </p>
            </div>

            {/* Generated PDF */}
            {generatedPdf && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-700">
                      تم إنشاء البروفايل بنجاح! ({generatedPdf.total_pages} صفحات)
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={previewPdf} size="sm" variant="outline">
                      <Eye className="h-4 w-4 ml-1" />
                      معاينة
                    </Button>
                    <Button onClick={downloadPdf} size="sm" className="bg-green-600 hover:bg-green-700">
                      <Download className="h-4 w-4 ml-1" />
                      تحميل
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Preview Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">معاينة هيكل البروفايل</CardTitle>
          <CardDescription>الصفحات التي سيتم إنشاؤها</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <div className="w-24 h-32 bg-gradient-to-b from-blue-900 to-blue-700 rounded-lg flex flex-col items-center justify-center text-white text-xs p-2">
              <Building2 className="h-6 w-6 mb-1" />
              <span>الغلاف</span>
            </div>
            <div className="w-24 h-32 bg-gray-100 rounded-lg flex flex-col items-center justify-center text-gray-600 text-xs p-2 border">
              <FileText className="h-6 w-6 mb-1" />
              <span>الفهرس</span>
            </div>
            <div className="w-24 h-32 bg-gray-100 rounded-lg flex flex-col items-center justify-center text-gray-600 text-xs p-2 border">
              <Building2 className="h-6 w-6 mb-1" />
              <span>نبذة الشركة</span>
            </div>
            <div className="w-24 h-32 bg-gray-100 rounded-lg flex flex-col items-center justify-center text-gray-600 text-xs p-2 border">
              <Image className="h-6 w-6 mb-1" />
              <span>المنتجات</span>
            </div>
            {selectedProducts.slice(0, 4).map((key, i) => {
              const product = products.find(p => p.key === key);
              return (
                <div key={key} className="w-24 h-32 bg-blue-50 rounded-lg flex flex-col items-center justify-center text-blue-700 text-xs p-2 border border-blue-200">
                  <Image className="h-6 w-6 mb-1" />
                  <span className="text-center line-clamp-2">{product?.name}</span>
                </div>
              );
            })}
            {selectedProducts.length > 4 && (
              <div className="w-24 h-32 bg-gray-200 rounded-lg flex flex-col items-center justify-center text-gray-600 text-xs p-2">
                <span>+{selectedProducts.length - 4}</span>
                <span>منتجات أخرى</span>
              </div>
            )}
            <div className="w-24 h-32 bg-gradient-to-b from-blue-700 to-blue-900 rounded-lg flex flex-col items-center justify-center text-white text-xs p-2">
              <Phone className="h-6 w-6 mb-1" />
              <span>اتصل بنا</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileTemplateGenerator;
